import { FormattedMessage, useIntl } from 'react-intl';
import offlineMapsSrc from '@alltrails/upsell/components/UpsellSystem/UpsellFeatureList/images/feature-thumb-offline-maps.avif';
import trailConditionsSrc from '@alltrails/upsell/components/UpsellSystem/UpsellFeatureList/images/feature-thumb-trail-conditions.avif';
import wrongTurnAlertsSrc from '@alltrails/upsell/components/UpsellSystem/UpsellFeatureList/images/feature-thumb-wrong-turn-alerts.avif';
import UpsellFeatureList from '@alltrails/upsell/components/UpsellSystem/UpsellFeatureList/UpsellFeatureList';
import corosSrc from './coros.avif';
import liveShareSrc from './feature-thumb-live-share.avif';
import memberDealsSrc from './feature-thumb-member-deals.avif';
import garminSrc from './feature-thumb-send-to-garmin.avif';

type Props = {
  isInUs: boolean;
  isRei?: boolean;
  memberDealsFirst?: boolean;
};

export default function PlusMembershipIncludes({ isInUs, isRei, memberDealsFirst }: Props) {
  const intl = useIntl();

  if (isRei) {
    return (
      <UpsellFeatureList
        features={[
          {
            headline: intl.formatMessage({ defaultMessage: 'Unlimited Map Downloads' }),
            subheadline: intl.formatMessage({ defaultMessage: 'Download trails, parks, and areas to stay on route without cell signal' }),
            thumbnailImage: offlineMapsSrc.src
          },
          {
            headline: intl.formatMessage({ defaultMessage: 'Real-time Trail Conditions' }),
            subheadline: intl.formatMessage({ defaultMessage: 'Plan for trail-specific weather, snow depth, and more' }),
            thumbnailImage: trailConditionsSrc.src
          },
          {
            headline: intl.formatMessage({ defaultMessage: 'Wrong-Turn Alerts' }),
            subheadline: intl.formatMessage({ defaultMessage: 'Focus on the beauty around you, not the map, with wrong-turn alerts' }),
            thumbnailImage: wrongTurnAlertsSrc.src
          },
          {
            headline: intl.formatMessage({ defaultMessage: 'Connect Garmin & COROS' }),
            subheadline: intl.formatMessage({
              defaultMessage: 'Sync your wearable to AllTrails to get routes on your wrist, track your activities, and more'
            }),
            thumbnailImage: corosSrc.src
          }
        ]}
        label={<FormattedMessage defaultMessage="Everything you need to discover, plan, and navigate" />}
      />
    );
  }

  const memberDeals = {
    headline: intl.formatMessage({ defaultMessage: 'Member Deals' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Exclusive savings on apparel, outdoor accommodations, flights, and more' }),
    thumbnailImage: memberDealsSrc.src
  };

  const offlineMaps = {
    headline: intl.formatMessage({ defaultMessage: 'Offline Maps' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Download maps so you always know where you are, even without cell service' }),
    thumbnailImage: offlineMapsSrc.src
  };

  const trailConditions = {
    headline: intl.formatMessage({ defaultMessage: 'Trail Conditions' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Plan for trail-specific weather, snow depth, and more' }),
    thumbnailImage: trailConditionsSrc.src
  };

  const wrongTurnAlerts = {
    headline: intl.formatMessage({ defaultMessage: 'Wrong-Turn Alerts' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Focus on the beauty around you, not the map, with wrong-turn alerts' }),
    thumbnailImage: wrongTurnAlertsSrc.src
  };

  const sendToGarmin = {
    headline: intl.formatMessage({ defaultMessage: 'Send Routes to Garmin' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Have the route at your wrist. Upload AllTrails routes to your Garmin device' }),
    thumbnailImage: garminSrc.src
  };

  const liveShare = {
    headline: intl.formatMessage({ defaultMessage: 'Live Share' }),
    subheadline: intl.formatMessage({ defaultMessage: 'Keep your loved ones in the loop by sharing your real-time trail activity' }),
    thumbnailImage: liveShareSrc.src
  };

  const usDefaultFeatures = [offlineMaps, trailConditions, memberDeals, wrongTurnAlerts, sendToGarmin];
  const usMemberDealsFirstFeatures = [memberDeals, offlineMaps, trailConditions, wrongTurnAlerts, sendToGarmin];
  const nonUsFeatures = [offlineMaps, trailConditions, wrongTurnAlerts, sendToGarmin, liveShare];
  const features = isInUs ? (memberDealsFirst ? usMemberDealsFirstFeatures : usDefaultFeatures) : nonUsFeatures;

  return <UpsellFeatureList features={features} label={<FormattedMessage defaultMessage="Plus membership includes" />} variant="compact" />;
}
